import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { api, money, getApiErrorMessage } from "@/lib/api";
import { dateLocale } from "@/lib/i18n";
import { Navbar, DashHeader, StatusBadge, PaymentBadge, Stars, VerifiedBadge } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Users, ShieldCheck, Briefcase, Star, BadgeCheck, CalendarCheck, Check, X, ExternalLink, IdCard, Undo2 } from "lucide-react";

export default function AdminDashboard() {
    const { t } = useTranslation();
    const [stats, setStats] = useState(null);
    const [pros, setPros] = useState([]);
    const [users, setUsers] = useState([]);
    const [bookings, setBookings] = useState([]);
    const [verifications, setVerifications] = useState([]);

    const load = useCallback(async () => {
        const [s, p, u, b, v] = await Promise.all([
            api.get("/admin/stats"),
            api.get("/admin/professionals"),
            api.get("/admin/users"),
            api.get("/admin/bookings"),
            api.get("/admin/verifications"),
        ]);
        setStats(s.data); setPros(p.data); setUsers(u.data); setBookings(b.data); setVerifications(v.data);
    }, []);

    useEffect(() => { load(); }, [load]);

    const toggleVerify = async (id, verified) => {
        await api.post(`/admin/professionals/${id}/verify`, { verified });
        toast.success(verified ? t("Professionista verificato") : t("Verifica rimossa"));
        load();
    };

    const refund = async (id) => {
        if (!window.confirm(t("Rimborsare al cliente l'intero importo pagato e annullare la prenotazione?"))) return;
        try {
            const { data } = await api.post(`/admin/bookings/${id}/refund`);
            toast.success(t("Rimborso effettuato ({{amount}})", { amount: data.refunded }));
            load();
        } catch (e) { toast.error(getApiErrorMessage(e)); }
    };

    const approveVerification = async (id) => {
        await api.post(`/admin/professionals/${id}/verification`, { approve: true });
        toast.success(t("Verifica approvata"));
        load();
    };
    const rejectVerification = async (id, note) => {
        await api.post(`/admin/professionals/${id}/verification`, { approve: false, note });
        toast.success(t("Verifica rifiutata"));
        load();
    };

    const cards = stats ? [
        { icon: Users, label: t("Utenti totali"), value: stats.users },
        { icon: Briefcase, label: t("Professionisti"), value: stats.professionals },
        { icon: BadgeCheck, label: t("Verificati"), value: stats.verified },
        { icon: IdCard, label: t("In verifica"), value: stats.pending_verifications },
        { icon: CalendarCheck, label: t("Prenotazioni"), value: stats.bookings },
        { icon: Star, label: t("Recensioni"), value: stats.reviews },
    ] : [];

    return (
        <div className="min-h-screen bg-cream">
            <Navbar />
            <div className="mx-auto max-w-7xl px-5 sm:px-8 py-10">
                <DashHeader title={t("Pannello amministrazione")} subtitle={t("Gestisci la piattaforma Ciapum.")} />

                <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-8">
                    {cards.map((c) => (
                        <div key={c.label} className="rounded-2xl border border-border bg-white p-5" data-testid={`stat-${c.label}`}>
                            <c.icon className="h-5 w-5 text-terracotta" />
                            <p className="mt-3 font-heading text-3xl font-black text-ink">{c.value}</p>
                            <p className="text-sm text-ink/60">{c.label}</p>
                        </div>
                    ))}
                </div>

                <Tabs defaultValue="pros">
                    <TabsList className="bg-creamalt w-full justify-start overflow-x-auto">
                        <TabsTrigger value="pros" data-testid="tab-admin-pros">{t("Professionisti")}</TabsTrigger>
                        <TabsTrigger value="verifications" data-testid="tab-admin-verifications">
                            {t("Verifiche")}
                            {verifications.length > 0 && (
                                <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-terracotta text-white text-[10px] font-bold">{verifications.length}</span>
                            )}
                        </TabsTrigger>
                        <TabsTrigger value="users" data-testid="tab-admin-users">{t("Utenti")}</TabsTrigger>
                        <TabsTrigger value="bookings" data-testid="tab-admin-bookings">{t("Prenotazioni")}</TabsTrigger>
                    </TabsList>

                    <TabsContent value="pros" className="mt-6">
                        <div className="rounded-2xl border border-border bg-white overflow-hidden">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>{t("Nome")}</TableHead><TableHead>{t("Categoria")}</TableHead><TableHead>{t("Città")}</TableHead>
                                        <TableHead>{t("Valutazione")}</TableHead><TableHead>{t("Verificato")}</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {pros.map((p) => (
                                        <TableRow key={p.id} data-testid={`admin-pro-${p.id}`}>
                                            <TableCell className="font-medium text-ink">{p.name}</TableCell>
                                            <TableCell>{p.category_name}</TableCell>
                                            <TableCell>{p.city}</TableCell>
                                            <TableCell><Stars value={p.rating} count={p.reviews_count} /></TableCell>
                                            <TableCell>
                                                <Switch checked={!!p.verified} onCheckedChange={(v) => toggleVerify(p.id, v)} data-testid={`verify-switch-${p.id}`} />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    </TabsContent>

                    <TabsContent value="verifications" className="mt-6">
                        {verifications.length === 0 ? (
                            <div className="rounded-2xl border border-dashed border-border bg-white py-16 text-center text-ink/50" data-testid="no-verifications">
                                <BadgeCheck className="h-10 w-10 mx-auto mb-3" />
                                <p>{t("Nessuna richiesta di verifica in sospeso.")}</p>
                            </div>
                        ) : (
                            <div className="grid gap-4 sm:grid-cols-2">
                                {verifications.map((v) => (
                                    <div key={v.id} className="rounded-2xl border border-border bg-white p-5" data-testid={`verification-${v.id}`}>
                                        <div className="flex items-start justify-between gap-3">
                                            <div>
                                                <p className="font-heading font-bold text-ink">{v.name}</p>
                                                <p className="text-sm text-terracotta">{v.category_name} · {v.city}</p>
                                            </div>
                                            <span className="text-xs text-ink/40 shrink-0">{v.verification_submitted_at ? new Date(v.verification_submitted_at).toLocaleDateString(dateLocale()) : ""}</span>
                                        </div>
                                        <a href={v.verification_doc_url} target="_blank" rel="noreferrer" className="mt-4 relative block rounded-xl overflow-hidden border border-border" data-testid={`verification-doc-${v.id}`}>
                                            <img src={v.verification_doc_url} alt={t("Documento d'identità")} className="w-full h-44 object-cover bg-creamalt" />
                                            <span className="absolute bottom-2 right-2 inline-flex items-center gap-1 rounded-full bg-black/60 text-white text-xs px-2 py-1"><ExternalLink className="h-3 w-3" /> {t("Apri")}</span>
                                        </a>
                                        <div className="mt-4 flex gap-2">
                                            <Button onClick={() => approveVerification(v.id)} className="flex-1 rounded-full bg-emerald-600 hover:bg-emerald-700" data-testid={`approve-${v.id}`}><Check className="h-4 w-4 mr-1" /> {t("Approva")}</Button>
                                            <RejectDialog onReject={(note) => rejectVerification(v.id, note)} testid={v.id} />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </TabsContent>

                    <TabsContent value="users" className="mt-6">
                        <div className="rounded-2xl border border-border bg-white overflow-hidden">
                            <Table>
                                <TableHeader>
                                    <TableRow><TableHead>{t("Nome")}</TableHead><TableHead>{t("Email")}</TableHead><TableHead>{t("Ruolo")}</TableHead><TableHead>{t("Città")}</TableHead></TableRow>
                                </TableHeader>
                                <TableBody>
                                    {users.map((u) => (
                                        <TableRow key={u.id} data-testid={`admin-user-${u.id}`}>
                                            <TableCell className="font-medium text-ink">{u.name}</TableCell>
                                            <TableCell>{u.email}</TableCell>
                                            <TableCell><span className="capitalize rounded-full bg-creamalt border border-border px-2.5 py-0.5 text-xs">{u.role}</span></TableCell>
                                            <TableCell>{u.city || "—"}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    </TabsContent>

                    <TabsContent value="bookings" className="mt-6">
                        <div className="rounded-2xl border border-border bg-white overflow-hidden">
                            <Table>
                                <TableHeader>
                                    <TableRow><TableHead>{t("Lavoro")}</TableHead><TableHead>{t("Cliente")}</TableHead><TableHead>{t("Professionista")}</TableHead><TableHead>{t("Prezzo")}</TableHead><TableHead>{t("Stato")}</TableHead><TableHead>{t("Pagamento")}</TableHead><TableHead>{t("Azioni")}</TableHead></TableRow>
                                </TableHeader>
                                <TableBody>
                                    {bookings.map((b) => (
                                        <TableRow key={b.id} data-testid={`admin-booking-${b.id}`}>
                                            <TableCell className="font-medium text-ink max-w-48 truncate">{b.description}</TableCell>
                                            <TableCell>{b.client_name}</TableCell>
                                            <TableCell>{b.professional?.name}</TableCell>
                                            <TableCell>{money(b.price, b.professional?.currency_symbol)}</TableCell>
                                            <TableCell><StatusBadge status={b.status} /></TableCell>
                                            <TableCell><PaymentBadge status={b.payment_status} /></TableCell>
                                            <TableCell>
                                                {["deposit_paid", "paid_escrow"].includes(b.payment_status) ? (
                                                    <Button size="sm" variant="outline" className="rounded-full border-rose-200 text-rose-700 hover:bg-rose-50" onClick={() => refund(b.id)} data-testid={`refund-${b.id}`}>
                                                        <Undo2 className="h-4 w-4 mr-1" /> {t("Rimborsa")}
                                                    </Button>
                                                ) : <span className="text-ink/30">—</span>}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    </TabsContent>
                </Tabs>
            </div>
        </div>
    );
}

function RejectDialog({ onReject, testid }) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [note, setNote] = useState("");
    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button variant="outline" className="flex-1 rounded-full border-rose-200 text-rose-700 hover:bg-rose-50" data-testid={`reject-btn-${testid}`}><X className="h-4 w-4 mr-1" /> {t("Rifiuta")}</Button>
            </DialogTrigger>
            <DialogContent className="bg-white max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle className="font-heading text-xl">{t("Rifiuta verifica")}</DialogTitle></DialogHeader>
                <div className="space-y-2">
                    <Label>{t("Motivo (mostrato al professionista)")}</Label>
                    <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder={t("Es. Documento non leggibile o scaduto.")} data-testid={`reject-note-${testid}`} />
                </div>
                <DialogFooter>
                    <Button onClick={() => { onReject(note); setOpen(false); setNote(""); }} className="w-full rounded-full bg-rose-600 hover:bg-rose-700" data-testid={`confirm-reject-${testid}`}>{t("Conferma rifiuto")}</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
